import React, { useState, useCallback } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Database, RefreshCw, Search, Eye } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { TestLog } from "./TestingComponent";

interface DatabaseInspectorProps {
  onTestLog: (log: Omit<TestLog, 'id' | 'timestamp'>) => void;
}

interface TableData {
  tableName: string;
  data: any[];
  count: number;
  error?: string;
}

export const DatabaseInspector: React.FC<DatabaseInspectorProps> = ({ onTestLog }) => {
  const { user } = useAuth();
  const [selectedTable, setSelectedTable] = useState<string>('calie_tenants');
  const [tableData, setTableData] = useState<TableData | null>(null);
  const [loading, setLoading] = useState(false);
  const [allTablesData, setAllTablesData] = useState<Record<string, TableData>>({});

  const calieTablesList = [
    'calie_tenants',
    'calie_users',
    'calie_onboarding_state',
    'calie_numbers',
    'calie_campaigns',
    'calie_ads',
    'calie_calls',
    'calie_recordings',
    'calie_sms_conversations',
    'calie_call_events',
    'calie_ad_numbers',
    'calie_campaign_numbers',
    'calie_tenant_members',
    'calie_agency_twilio_creds',
    'calie_twilio_config',
  ];

  const log = useCallback((level: TestLog['level'], message: string, details?: any) => {
    onTestLog({ level, message, details });
  }, [onTestLog]);

  const inspectTable = useCallback(async (tableName: string) => {
    setLoading(true);
    log('info', `Inspecting table: ${tableName}`);

    try {
      // Query the table data
      const { data, error, count } = await supabase
        .from(tableName as any)
        .select('*', { count: 'exact' })
        .limit(100); // Limit to prevent overwhelming the UI

      if (error) {
        throw error;
      }

      const tableResult: TableData = {
        tableName,
        data: data || [],
        count: count || 0,
      };

      setTableData(tableResult);
      setAllTablesData(prev => ({ ...prev, [tableName]: tableResult }));

      log('success', `Successfully inspected ${tableName}`, {
        rowCount: count,
        sampleData: data?.slice(0, 3) // Log first 3 rows as sample
      });

    } catch (error: any) {
      const errorResult: TableData = {
        tableName,
        data: [],
        count: 0,
        error: error.message,
      };

      setTableData(errorResult);
      log('error', `Failed to inspect ${tableName}`, error);
    } finally {
      setLoading(false);
    }
  }, [log]);

  const inspectAllTables = useCallback(async () => {
    setLoading(true);
    log('info', 'Starting comprehensive database inspection...');

    const results: Record<string, TableData> = {};

    for (const tableName of calieTablesList) {
      try {
        log('debug', `Inspecting ${tableName}...`);
        
        const { data, error, count } = await supabase
          .from(tableName as any)
          .select('*', { count: 'exact' })
          .limit(50);

        if (error) {
          results[tableName] = {
            tableName,
            data: [],
            count: 0,
            error: error.message,
          };
          log('warn', `Error inspecting ${tableName}: ${error.message}`);
        } else {
          results[tableName] = {
            tableName,
            data: data || [],
            count: count || 0,
          };
          log('debug', `${tableName}: ${count} rows`);
        }
      } catch (error: any) {
        results[tableName] = {
          tableName,
          data: [],
          count: 0,
          error: error.message,
        };
        log('error', `Exception inspecting ${tableName}`, error);
      }
    }

    setAllTablesData(results);
    
    // Set the currently selected table data
    if (results[selectedTable]) {
      setTableData(results[selectedTable]);
    }

    const summary = {
      totalTables: calieTablesList.length,
      successfulTables: Object.values(results).filter(r => !r.error).length,
      failedTables: Object.values(results).filter(r => r.error).length,
      totalRows: Object.values(results).reduce((sum, r) => sum + r.count, 0),
    };

    log('success', 'Database inspection completed', summary);
    setLoading(false);
  }, [selectedTable, log]);

  const validateDataIntegrity = useCallback(async () => {
    setLoading(true);
    log('info', 'Running data integrity checks...');

    try {
      const issues: string[] = [];

      // Check for orphaned records
      log('debug', 'Checking for orphaned campaign numbers...');
      const { data: orphanedCampaignNumbers } = await supabase
        .from('calie_campaign_numbers')
        .select(`
          campaign_id,
          number_id,
          calie_campaigns!inner(campaign_id),
          calie_numbers!inner(number_id)
        `);

      // Check for missing required data
      log('debug', 'Checking for incomplete onboarding states...');
      const { data: incompleteOnboarding, error: incompleteError } = await supabase
        .from('calie_onboarding_state')
        .select('*')
        .not('onboarding_completed_at', 'is', null)
        .or('forwarding_number.is.null,current_step.eq.done');

      if (incompleteError) {
        log('warn', 'Error checking incomplete onboarding states', incompleteError);
      }

      if (incompleteOnboarding && incompleteOnboarding.length > 0) {
        issues.push(`${incompleteOnboarding.length} tenants marked as complete but missing required data`);
      }

      // Check for tenant consistency
      log('debug', 'Checking tenant data consistency...');
      const { data: tenantsWithoutUsers } = await supabase
        .from('calie_tenants')
        .select(`
          tenant_id,
          calie_users!inner(tenant_id)
        `);

      const summary = {
        issuesFound: issues.length,
        issues,
        checksRun: 3,
        timestamp: new Date().toISOString(),
      };

      if (issues.length === 0) {
        log('success', 'Data integrity check passed - no issues found', summary);
      } else {
        log('warn', 'Data integrity issues detected', summary);
      }

    } catch (error: any) {
      log('error', 'Data integrity check failed', error);
    } finally {
      setLoading(false);
    }
  }, [log]);

  const formatValue = (value: any): string => {
    if (value === null) return 'NULL';
    if (value === undefined) return 'UNDEFINED';
    if (typeof value === 'object') return JSON.stringify(value, null, 2);
    if (typeof value === 'boolean') return value.toString().toUpperCase();
    return String(value);
  };

  const getColumnType = (value: any): string => {
    if (value === null || value === undefined) return 'null';
    if (typeof value === 'object' && value.constructor === Object) return 'json';
    if (Array.isArray(value)) return 'array';
    if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/.test(value)) return 'timestamp';
    return typeof value;
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Database className="h-5 w-5 text-primary" />
            <CardTitle>Database Inspector</CardTitle>
          </div>
          <CardDescription>
            Inspect Calie.ai database tables, validate data integrity, and monitor RLS policies
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Select value={selectedTable} onValueChange={setSelectedTable}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {calieTablesList.map((table) => (
                  <SelectItem key={table} value={table}>
                    {table}
                    {allTablesData[table] && (
                      <Badge className="ml-2" variant="outline">
                        {allTablesData[table].count}
                      </Badge>
                    )}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Button
              onClick={() => inspectTable(selectedTable)}
              disabled={loading}
              variant="outline"
            >
              <Eye className="h-4 w-4 mr-2" />
              Inspect Table
            </Button>

            <Button
              onClick={inspectAllTables}
              disabled={loading}
              variant="outline"
            >
              <Search className="h-4 w-4 mr-2" />
              Inspect All
            </Button>
          </div>

          <div className="flex gap-2">
            <Button
              onClick={validateDataIntegrity}
              disabled={loading}
              className="flex-1"
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Run Integrity Checks
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Table Data Display */}
      {tableData && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>{tableData.tableName}</CardTitle>
                <CardDescription>
                  {tableData.error 
                    ? `Error: ${tableData.error}`
                    : `${tableData.count} rows total, showing first 100`
                  }
                </CardDescription>
              </div>
              <Badge variant={tableData.error ? "destructive" : "secondary"}>
                {tableData.error ? "Error" : `${tableData.count} rows`}
              </Badge>
            </div>
          </CardHeader>
          <CardContent>
            {tableData.error ? (
              <div className="text-destructive p-4 bg-destructive/10 rounded border border-destructive/20">
                <strong>Error:</strong> {tableData.error}
              </div>
            ) : (
              <ScrollArea className="h-96">
                {tableData.data.length === 0 ? (
                  <div className="text-center text-muted-foreground py-8">
                    No data in this table
                  </div>
                ) : (
                  <div className="space-y-4">
                    {tableData.data.map((row, index) => (
                      <div key={index} className="border rounded-lg p-4 space-y-2">
                        <div className="flex items-center gap-2 mb-2">
                          <Badge variant="outline">Row {index + 1}</Badge>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          {Object.entries(row).map(([key, value]) => (
                            <div key={key} className="space-y-1">
                              <div className="flex items-center gap-2">
                                <span className="text-sm font-medium">{key}</span>
                                <Badge variant="outline" className="text-xs">
                                  {getColumnType(value)}
                                </Badge>
                              </div>
                              <div className="text-sm text-muted-foreground bg-muted p-2 rounded">
                                {getColumnType(value) === 'json' ? (
                                  <pre className="text-xs overflow-auto">
                                    {formatValue(value)}
                                  </pre>
                                ) : (
                                  <span className="break-all">
                                    {formatValue(value)}
                                  </span>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </ScrollArea>
            )}
          </CardContent>
        </Card>
      )}

      {/* Summary of all tables */}
      {Object.keys(allTablesData).length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Database Overview</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {Object.entries(allTablesData).map(([tableName, data]) => (
                <div
                  key={tableName}
                  className={`p-3 border rounded-lg cursor-pointer hover:bg-muted/50 ${
                    selectedTable === tableName ? 'ring-2 ring-primary' : ''
                  }`}
                  onClick={() => setSelectedTable(tableName)}
                >
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-sm">{tableName}</span>
                    <Badge variant={data.error ? "destructive" : "secondary"}>
                      {data.error ? "Error" : data.count}
                    </Badge>
                  </div>
                  {data.error && (
                    <div className="text-xs text-destructive mt-1 truncate">
                      {data.error}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};